'use client';

import { useState } from 'react';
import Button from '@/components/Button';
import SectionTitle from '@/components/SectionTitle';
import Subheader from '@/components/Subheader';
import Cards from '@/components/Cards';
import { useConfig } from '@/app/contexts/ConfigContext';
import { FITNESS_WORDING_EN } from '@/wording/fitness/FITNESS_WORDING_EN';

import { CardDefinition, ServiceType } from '@/types';
import Filters from '@/components/Filters';
import CustomLink from '@/components/CustomLink';
import { SERVICE_TYPES } from '@/helpers/enums';

interface MediaProps {
  items: CardDefinition[][];
}

export default function MediaSectionMultiFitnessYogaPilates({
  items,
}: MediaProps) {
  const { wording, config, setConfig, updateWording, selectedLanguage } =
    useConfig();

  const fitnessItems = items[0] ?? [];
  const yogaItems = items[1] ?? [];
  const pilatesItems = items[2] ?? [];

  const [selectedState, setSelectedState] = useState<string>(
    FITNESS_WORDING_EN.text_media_1
  );

  const MediaContentLinkUrl = () => {
    if (config.serviceType !== SERVICE_TYPES.FITNESS) {
      return '/media/media_all';
    } else {
      return selectedState === FITNESS_WORDING_EN.text_media_1
        ? '/media/media_1'
        : '/media/media_2';
    }
  };

  const handleConfig = (type: ServiceType) => {
    setConfig({
      ...config,
      serviceType: type,
    });
    updateWording(type, selectedLanguage);
  };

  return (
    <section className='sm:mb-32 mb-8'>
      {/* ---------- Fitness ---------- */}
      <SectionTitle>{wording?.media_content_section_title}</SectionTitle>
      <Subheader className='mt-4'>
        {/* {wording?.media_content_section_subheader_text} */}
        {wording?.media_content_section_multi_1_subheader_text}
      </Subheader>
      <div className='flex items-end justify-between items-center mt-4'>
        <div className='flex sm:gap-12 gap-6'>
          <Filters
            options={[
              {
                // Name needs not to be using wording for this dedicated fitness/yoga/pilates case or it will not be visible since we don't have this on some services
                name: wording.text_media_1,
                value: FITNESS_WORDING_EN.text_media_1,
              },
              {
                // Name needs not to be using wording for this dedicated fitness/yoga/pilates case or it will not be visible since we don't have this on some services
                name: wording.text_media_2,
                value: FITNESS_WORDING_EN.text_media_2,
              },
            ]}
            activeOption={selectedState}
            onSelect={(value) => setSelectedState(value)}
          />
        </div>
        <Button
          variant='secondary'
          className='!px-1 py-2 w-[125px] sm:!w-[295px]'
          themeBackgroundColor={config.theme.textColorDark}
          themeTextColor={config.theme.textColorLight}
        >
          <CustomLink
            href={MediaContentLinkUrl()}
            onClick={() => {
              handleConfig(SERVICE_TYPES.FITNESS);
            }}
          >
            {wording?.media_content_section_button_more}
          </CustomLink>
        </Button>
      </div>
      <Cards
        items={fitnessItems.filter((item) => item.tags.includes(selectedState))}
        serviceType={SERVICE_TYPES.FITNESS}
      />

      {/* ---------- Yoga ---------- */}
      <div className='flex items-end justify-between items-center mt-4'>
        <Subheader>
          {/* {wording?.media_content_section_subheader_text} */}
          {wording?.media_content_section_multi_2_subheader_text}
        </Subheader>

        <div className='flex sm:gap-12 gap-6'></div>
        <Button
          variant='secondary'
          className='!px-1 py-2 w-[125px] sm:!w-[295px]'
          themeBackgroundColor={config.theme.textColorDark}
          themeTextColor={config.theme.textColorLight}
        >
          <CustomLink
            href='/media/media_all'
            onClick={() => {
              handleConfig(SERVICE_TYPES.YOGA);
            }}
          >
            {wording?.media_content_section_button_more}
          </CustomLink>
        </Button>
      </div>
      <Cards items={yogaItems} serviceType={SERVICE_TYPES.YOGA} />

      {/* ---------- Pilates ----------*/}
      <div className='flex items-end justify-between items-center mt-4'>
        <Subheader>
          {/* {wording?.media_content_section_subheader_text} */}
          {wording?.media_content_section_multi_3_subheader_text}
        </Subheader>

        <div className='flex sm:gap-12 gap-6'></div>
        <Button
          variant='secondary'
          className='!px-1 py-2 w-[125px] sm:!w-[295px]'
          themeBackgroundColor={config.theme.textColorDark}
          themeTextColor={config.theme.textColorLight}
        >
          <CustomLink
            href='/media/media_all'
            onClick={() => {
              handleConfig(SERVICE_TYPES.PILATES);
            }}
          >
            {wording?.media_content_section_button_more}
          </CustomLink>
        </Button>
      </div>
      <Cards items={pilatesItems} serviceType={SERVICE_TYPES.PILATES} />
    </section>
  );
}
